/**
 * wsClient — real WebSocket client against the PartyServer GameRoom DO.
 *
 * Connects to `${VITE_WS_BASE_URL}/parties/main/${sessionId}` (default
 * `ws://localhost:8787`). On open, replays the stored auth token (if any)
 * via the `?token=` query parameter so the server can reattach an existing
 * player slot, then sends a `JoinSession` handshake with name + colour.
 *
 * Auto-reconnects with exponential backoff (1s → 2s → 4s → 8s → 16s → 30s
 * cap) on unexpected close. The server-issued `Hello` is the source of
 * truth for the AuthToken which we persist to `localStorage` keyed by
 * session id.
 *
 * **Liveness detection (heartbeat).** Mobile browsers and OSes routinely
 * suspend WebSocket connections when the tab is backgrounded — TCP keeps
 * the socket "open" from JavaScript's perspective while bytes are silently
 * dropped, so the `close` event never fires and the user sees stale state.
 * To detect this:
 *   - Send `ping` every PING_INTERVAL_MS (with a nonce).
 *   - If no `pong` arrives within PONG_TIMEOUT_MS, force-close the socket;
 *     the existing reconnect path then opens a fresh one and the server
 *     replays Hello + LobbyState + GameState, catching the user up.
 *   - On `document.visibilitychange` to `visible`, fire an immediate ping
 *     so a returning user gets caught up within seconds rather than after
 *     the next 20s tick.
 *
 * Incoming messages are NOT validated against the full ServerMsg union
 * shape (the protocol only ships an `isClientMsg` guard); we do a minimal
 * structural check ({ type: string }) and warn on garbage.
 *
 * Multi-store fan-out:
 *   `onMessage` is a Set-backed event bus, so multiple consumers may
 *   register handlers for the same client without stepping on each
 *   other. Concretely:
 *     - lobbyStore subscribes to filter `lobbyState`, `hello`, `error`.
 *     - gameStore  subscribes to filter `gameState` frames.
 *   Both receive every ServerMsg; each ignores types it doesn't care
 *   about. This keeps the transport layer decoupled from the store
 *   layer — a future spectator/event-log store can attach the same
 *   way without touching this file.
 *
 * Pending-send queue:
 *   Sends issued while the socket is `connecting` or `reconnecting` are
 *   buffered into a small FIFO (cap 32, drop-oldest) and flushed on the
 *   next `open` event. This fixes a race where the lobby→play route
 *   transition tears down the previous socket and the play route's
 *   auto-pilot useEffect dispatches its first `endPhase` before the new
 *   socket has finished its handshake.
 *
 *   Idempotency contract: messages may be re-sent if a transient
 *   disconnect occurs after the server has already received them.
 *   Quality Domination's gameReducer is idempotent at the action level
 *   (a duplicated DEPLOY_CUBES with empty reserves is rejected with
 *   `wrong_phase` / `not_your_turn`), so this is safe. Callers MUST keep
 *   their actions idempotent for the queue to remain correct.
 */

import {
  asAuthToken,
  type AuthToken,
  type ClientMsg,
  type PlayerColorKey,
  type ServerMsg,
  type SessionId,
} from '@qd/protocol';

// -----------------------------------------------------------------------------
// Public types
// -----------------------------------------------------------------------------

/** Connection lifecycle from the consumer's perspective. */
export type WsState = 'connecting' | 'open' | 'closed' | 'reconnecting';

export interface WsClient {
  /** Send a typed client message. No-op if the socket isn't open. */
  send(msg: ClientMsg): void;
  /** Subscribe to incoming server messages. Returns an unsubscribe fn. */
  onMessage(cb: (msg: ServerMsg) => void): () => void;
  /** Subscribe to state-machine transitions. Returns an unsubscribe fn. */
  onStateChange(cb: (state: WsState) => void): () => void;
  /** Close cleanly. Cancels any pending reconnect. */
  close(): void;
  /** Read the current connection state (for UI overlays). */
  getState(): WsState;
}

export interface ConnectOptions {
  /** Word-coded session id from the server, used as the DO room name. */
  sessionId: SessionId;
  /**
   * Auth token from a previous session (server-issued). Optional; absent
   * tokens trigger fresh-connect on the server side.
   */
  token?: AuthToken;
  /**
   * Initial join payload sent immediately on each (re)connect after the
   * server's `Hello`. The server uses this to seat the player in the lobby
   * roster on first connect, and ignores it on reconnect (it only flips
   * the `isConnected` flag).
   */
  join?: { name?: string; color?: PlayerColorKey };
}

// -----------------------------------------------------------------------------
// localStorage helpers (token persistence)
// -----------------------------------------------------------------------------

const TOKEN_PREFIX = 'qd_token_';

/**
 * Maximum number of `ClientMsg` frames buffered while the socket is
 * `connecting` / `reconnecting`. When the queue exceeds this size we
 * evict the OLDEST entry (drop-oldest policy) — the most recent
 * dispatch reflects the user's current intent, while a stale enqueued
 * action from 30s ago is almost certainly noise the user has moved on
 * from. Set deliberately small: the only realistic burst comes from
 * the auto-pilot useEffect re-firing during a route transition, which
 * fires a handful of frames at most.
 */
const MAX_PENDING_QUEUE = 32;

function loadToken(sessionId: SessionId): AuthToken | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(TOKEN_PREFIX + sessionId);
    return raw ? asAuthToken(raw) : undefined;
  } catch {
    return undefined;
  }
}

function saveToken(sessionId: SessionId, token: AuthToken): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TOKEN_PREFIX + sessionId, token);
  } catch {
    // localStorage may be disabled (Safari private mode); silently degrade.
  }
}

// -----------------------------------------------------------------------------
// URL builder
// -----------------------------------------------------------------------------

function getWsBaseUrl(): string {
  // Vite injects `import.meta.env` at build time. The `as unknown` cast is
  // needed because the standard ImportMeta type doesn't include `env`.
  const envBase = (import.meta as unknown as { env?: { VITE_WS_BASE_URL?: string } }).env
    ?.VITE_WS_BASE_URL;
  return envBase ?? 'ws://localhost:8787';
}

function buildWsUrl(sessionId: SessionId, token?: AuthToken): string {
  const base = getWsBaseUrl();
  const path = `/parties/main/${encodeURIComponent(sessionId)}`;
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${base}${path}${q}`;
}

// -----------------------------------------------------------------------------
// Backoff schedule
// -----------------------------------------------------------------------------

/** Exponential backoff capped at 30s. Step `attempt` is 0-indexed. */
function backoffDelayMs(attempt: number): number {
  // 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...
  const raw = 1000 * 2 ** attempt;
  return Math.min(raw, 30_000);
}

// -----------------------------------------------------------------------------
// Heartbeat tuning
// -----------------------------------------------------------------------------

/** How often we send a ping while the socket is open. */
const PING_INTERVAL_MS = 20_000;
/** Pong must arrive within this window or we declare the socket dead. */
const PONG_TIMEOUT_MS = 8_000;
/** When the tab returns to foreground, give pong this much grace before reconnecting. */
const VISIBILITY_PROBE_TIMEOUT_MS = 4_000;

// -----------------------------------------------------------------------------
// Public connect()
// -----------------------------------------------------------------------------

export function connect(options: ConnectOptions): WsClient {
  const { sessionId, join } = options;

  // Token may evolve over the lifetime of a connection (server issues a
  // fresh one on first connect via `Hello`). We track the live value in
  // closure state and persist on update.
  let token: AuthToken | undefined = options.token ?? loadToken(sessionId);

  const messageHandlers = new Set<(msg: ServerMsg) => void>();
  const stateHandlers = new Set<(state: WsState) => void>();

  let socket: WebSocket | null = null;
  let state: WsState = 'connecting';
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let intentionallyClosed = false;
  /**
   * Monotonic socket generation. Each `openSocket()` bumps it; each socket's
   * event handlers capture their own generation and bail early if a newer
   * socket has taken over. Prevents a stale socket's `close` from nulling
   * out the live one when forceReconnect is called mid-CONNECTING.
   */
  let socketGen = 0;

  // Heartbeat machinery.
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let pongDeadline: ReturnType<typeof setTimeout> | null = null;
  let pendingNonce: number | null = null;

  /**
   * FIFO buffer for sends issued while the socket isn't yet `OPEN`.
   * Survives transient reconnects (the closure outlives any one socket
   * instance) and is drained on the next `open` event. Cleared only on
   * intentional `close()` — the WS-level `close` event preserves it so
   * messages dispatched during a flap survive the reconnect.
   * Bounded — see `MAX_PENDING_QUEUE`.
   */
  const pendingQueue: ClientMsg[] = [];

  function setState(next: WsState): void {
    if (state === next) return;
    state = next;
    for (const h of stateHandlers) h(next);
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function scheduleReconnect(): void {
    clearReconnectTimer();
    const delay = backoffDelayMs(attempt);
    attempt += 1;
    setState('reconnecting');
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openSocket();
    }, delay);
  }

  // ── Heartbeat helpers ───────────────────────────────────────────────────

  function clearPongDeadline(): void {
    if (pongDeadline != null) {
      clearTimeout(pongDeadline);
      pongDeadline = null;
    }
    pendingNonce = null;
  }

  function stopHeartbeat(): void {
    if (pingTimer != null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    clearPongDeadline();
  }

  function sendPing(timeoutMs: number): void {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    // Don't pile up pongs — a stale outstanding ping already counts.
    if (pendingNonce !== null) return;
    const nonce = Math.floor(Math.random() * 1e9);
    pendingNonce = nonce;
    try {
      socket.send(JSON.stringify({ type: 'ping', nonce }));
    } catch {
      // If the send throws, the close handler will fire shortly after.
      pendingNonce = null;
      return;
    }
    if (pongDeadline != null) clearTimeout(pongDeadline);
    pongDeadline = setTimeout(() => {
      // Pong didn't come back — assume the socket is dead-but-open
      // (typical after a mobile sleep). Force a reconnect.
      pendingNonce = null;
      pongDeadline = null;
      forceReconnect();
    }, timeoutMs);
  }

  function startHeartbeat(): void {
    stopHeartbeat();
    pingTimer = setInterval(() => sendPing(PONG_TIMEOUT_MS), PING_INTERVAL_MS);
  }

  function handlePong(nonce: unknown): void {
    if (typeof nonce === 'number' && pendingNonce !== null && nonce !== pendingNonce) {
      // Stale pong from a previous probe — ignore.
      return;
    }
    clearPongDeadline();
  }

  function forceReconnect(): void {
    if (intentionallyClosed) return;
    stopHeartbeat();
    // Reset backoff so the reconnect is immediate, not delayed.
    attempt = 0;
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        socket.close(4001, 'heartbeat-timeout');
      } catch {
        // ignore
      }
    } else {
      // Already closed/closing; just kick off a fresh attempt.
      clearReconnectTimer();
      openSocket();
    }
  }

  // ── Visibility handling: wake-from-background pokes the socket ──────────

  function onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    if (intentionallyClosed) return;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      // Already disconnected — skip backoff and try now.
      attempt = 0;
      clearReconnectTimer();
      openSocket();
      return;
    }
    // Socket *appears* alive; probe with a tighter pong window so a
    // returning user gets caught up in seconds rather than 20s.
    sendPing(VISIBILITY_PROBE_TIMEOUT_MS);
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function dispatchMessage(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // eslint-disable-next-line no-console
      console.warn('[wsClient] dropped non-JSON frame', raw);
      return;
    }
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { type?: unknown }).type !== 'string'
    ) {
      // eslint-disable-next-line no-console
      console.warn('[wsClient] dropped malformed message', parsed);
      return;
    }

    const msg = parsed as ServerMsg;
    // Heartbeat reply — clear the pending pong deadline before fan-out so
    // a slow consumer can't accidentally trigger a forceReconnect race.
    if (msg.type === 'pong') {
      handlePong((msg as { nonce?: unknown }).nonce);
    }
    // Capture the server-issued AuthToken from the `Hello` so we can replay
    // it on the next reconnect.
    if (msg.type === 'hello') {
      token = msg.token;
      saveToken(sessionId, msg.token);
      // After we know we're authenticated, send the JoinSession handshake
      // (name + colour) so the server seats us in the lobby roster on first
      // connect. Reconnects use the same payload — the server treats the
      // existing-player branch as a no-op for name/colour. We construct
      // the message conditionally to honour `exactOptionalPropertyTypes`.
      if (join) {
        const handshake: ClientMsg = {
          type: 'joinSession',
          token,
          ...(join.name !== undefined ? { name: join.name } : {}),
          ...(join.color !== undefined ? { color: join.color } : {}),
        };
        sendInternal(handshake);
      }
      // If no join payload is provided, the caller is expected to send
      // `joinSession` manually via send() once they have a name + colour
      // (this is how the JoinFlow stepper drives the admin path).
    }
    for (const h of messageHandlers) h(msg);
  }

  function sendInternal(msg: ClientMsg): void {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg));
      return;
    }

    // Permanently closed: nothing will flush this queue, so drop loudly.
    if (intentionallyClosed) {
      // eslint-disable-next-line no-console
      console.warn('[wsClient] dropped send on closed client', msg);
      return;
    }

    // Connecting or reconnecting (or constructed-but-not-yet-open): buffer
    // for the next `open` event. This fixes the race where the play route's
    // auto-pilot useEffect dispatches an action before the new socket has
    // finished its handshake. See file-level JSDoc for idempotency contract.
    if (state === 'connecting' || state === 'reconnecting') {
      pendingQueue.push(msg);
      if (pendingQueue.length > MAX_PENDING_QUEUE) {
        const dropped = pendingQueue.shift();
        // eslint-disable-next-line no-console
        console.warn(
          '[wsClient] pendingQueue overflow, dropping oldest',
          dropped,
        );
      }
      // eslint-disable-next-line no-console
      console.debug(
        '[wsClient] queued send while not open',
        msg,
        `(queue=${pendingQueue.length})`,
      );
      return;
    }

    // Defensive fallback: socket is null and state is something we didn't
    // anticipate (shouldn't happen given the state machine, but log if it
    // does so the failure mode is obvious).
    // eslint-disable-next-line no-console
    console.warn('[wsClient] dropped send in unexpected state', state, msg);
  }

  function flushPendingQueue(ws: WebSocket): void {
    if (pendingQueue.length === 0) return;
    const drained = pendingQueue.length;
    // FIFO drain. Splice to avoid re-allocating; we want the queue empty
    // after a successful flush.
    for (const msg of pendingQueue) {
      try {
        ws.send(JSON.stringify(msg));
      } catch (err) {
        // If the socket dies mid-flush the close handler will fire and the
        // remainder will get re-flushed on the next open. Re-queue the
        // un-sent tail by truncating to what we've already sent — but
        // since we don't know which one threw, conservatively keep the
        // failing message and everything after, drop everything before.
        // This is best-effort; in practice ws.send only throws if the
        // socket is already CLOSING/CLOSED, in which case the close
        // handler will run and reconnect anyway.
        // eslint-disable-next-line no-console
        console.warn('[wsClient] flush failed mid-drain', err);
        return;
      }
    }
    pendingQueue.length = 0;
    // eslint-disable-next-line no-console
    console.debug('[wsClient] flushed pendingQueue', `(count=${drained})`);
  }

  function openSocket(): void {
    if (intentionallyClosed) return;
    // If a previous socket is still hanging around (e.g. forceReconnect
    // called mid-CONNECTING), close it explicitly so its handlers don't
    // race with the new one. The generation check below makes those
    // handlers no-ops, but closing also frees the OS-level resource.
    if (socket) {
      try {
        socket.close(4000, 'reopen');
      } catch {
        // ignore
      }
    }
    // Defensive: clear any heartbeat machinery from a previous socket
    // before installing a new one. The `close` handler clears it too,
    // but if the prior socket was in CONNECTING (never opened) the
    // close event's generation guard skips the cleanup — calling here
    // unconditionally guarantees no stale ping interval survives.
    stopHeartbeat();
    socketGen += 1;
    const myGen = socketGen;
    setState('connecting');
    const url = buildWsUrl(sessionId, token);
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      // Construction failure (e.g. malformed URL); back off and retry.
      // eslint-disable-next-line no-console
      console.warn('[wsClient] WebSocket constructor failed, retrying', err);
      scheduleReconnect();
      return;
    }
    socket = ws;

    ws.addEventListener('open', () => {
      if (myGen !== socketGen) return; // stale socket — ignore
      setState('open');
      attempt = 0; // reset backoff on a successful connection.
      startHeartbeat();
      // Drain anything the caller dispatched while we were handshaking.
      // Note: this fires BEFORE the server's `Hello`, so the internal
      // `joinSession` handshake (which is sent in response to `Hello`)
      // takes the OPEN fast-path in `sendInternal` and is never enqueued.
      flushPendingQueue(ws);
    });

    ws.addEventListener('message', (event) => {
      if (myGen !== socketGen) return; // stale socket — ignore
      const data = typeof event.data === 'string' ? event.data : '';
      if (data) dispatchMessage(data);
    });

    ws.addEventListener('error', () => {
      // The 'close' event fires immediately after; let it drive reconnect.
    });

    ws.addEventListener('close', () => {
      if (myGen !== socketGen) return; // stale socket — newer one took over
      socket = null;
      stopHeartbeat();
      if (intentionallyClosed) {
        setState('closed');
        return;
      }
      // Transient drop: preserve `pendingQueue` so frames dispatched
      // during the flap survive the reconnect. The next successful
      // `open` will flush them.
      scheduleReconnect();
    });
  }

  // Kick off the first connection attempt synchronously.
  openSocket();

  return {
    send(msg: ClientMsg) {
      sendInternal(msg);
    },
    onMessage(cb) {
      messageHandlers.add(cb);
      return () => {
        messageHandlers.delete(cb);
      };
    },
    onStateChange(cb) {
      stateHandlers.add(cb);
      return () => {
        stateHandlers.delete(cb);
      };
    },
    close() {
      intentionallyClosed = true;
      clearReconnectTimer();
      stopHeartbeat();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.close(1000, 'client-close');
        } catch {
          // ignore
        }
      }
      socket = null;
      // Caller has explicitly given up — anything still queued is now
      // irrelevant (and we have nothing to flush it to anyway). The
      // transient-close path through the WS `close` event preserves the
      // queue; only an intentional `client.close()` clears it.
      pendingQueue.length = 0;
      setState('closed');
      messageHandlers.clear();
      stateHandlers.clear();
    },
    getState() {
      return state;
    },
  };
}
